<template>
  <div class="ebm-form">
    <Form
      @submit="onSubmit"
      :validation-schema="updateEbmSettingsSchema"
      :initial-values="initialValues"
      class="ebm-form__content"
    >
      <hs-form-grid :cols="2">
        <hs-field name="tin" type="text" label="TIN" placeholder="Enter TIN" :required="true" :model-value="formData.tin" />
        <hs-field name="bhfId" type="text" label="BHF ID" placeholder="Enter BHF ID" :required="true" :model-value="formData.bhfId" />
        <hs-field name="dvcSrlNo" type="text" label="DVC SRL No" placeholder="Enter DVC SRL No" :required="true" :model-value="formData.dvcSrlNo" />
        <hs-field name="mrc" type="text" label="MRC" placeholder="Enter MRC" :required="true" :model-value="formData.mrc" />
      </hs-form-grid>

      <div class="ebm-form__toggles">
        <hs-checkbox v-model="formData.ebm_activated" label="EBM Activated" description="Activate EBM for this store" />
        <hs-checkbox v-model="formData.vat_activated" label="VAT Activated" description="Enable VAT for transactions" />
      </div>

      <hs-field name="topMsg" type="text" label="Top Message" placeholder="Enter Top Message" :model-value="formData.topMsg" />
      <hs-field name="btmMsg" type="text" label="Bottom Message" placeholder="Enter Bottom Message" :required="true" :model-value="formData.btmMsg" />

      <div class="ebm-form__files">
        <div>
          <hs-file-upload
            v-model="rdb"
            title="RDB Certificate *"
            description="Upload your RDB certificate (required)"
            accept=".pdf,.jpg,.jpeg,.png"
            @change="handleRdbChange"
          />
          <p v-if="errors.rdb" class="ebm-form__file-error">{{ errors.rdb }}</p>
        </div>
        <div>
          <hs-file-upload
            v-model="otherFiles"
            title="Other Supporting Documents"
            description="Upload additional documents (optional, max 2)"
            accept=".pdf,.jpg,.jpeg,.png"
            :multiple="true"
            @change="handleOtherChange"
          />
        </div>
      </div>

      <hs-button type="submit" variant="primary" :loading="loading" class="ebm-form__submit">Send Request</hs-button>
    </Form>
  </div>
</template>

<script>
import { Form } from "vee-validate";
import { updateEbmSettingsSchema } from "@/validations/updateSettings";
import { HsButton, HsCheckbox, HsFileUpload, HsFormGrid } from "@/components/ui";

export default {
  name: "RequestToChangeEbmSettings",
  components: { Form, HsButton, HsCheckbox, HsFileUpload, HsFormGrid },
  props: {
    settings: { type: Object, required: true },
    loading: { type: Boolean, default: false },
  },
  emits: ["update-settings", "close-modal"],
  data() {
    return {
      formData: {
        tin: "",
        bhfId: "",
        dvcSrlNo: "",
        mrc: "",
        ebm_activated: false,
        vat_activated: false,
        topMsg: "",
        btmMsg: "",
      },
      rdb: null,
      otherFiles: null,
      errors: { rdb: null },
    };
  },
  computed: {
    initialValues() {
      if (this.settings) {
        return {
          tin: this.settings.tin || "",
          bhfId: this.settings.bhfId || "",
          dvcSrlNo: this.settings.dvcSrlNo || "",
          mrc: this.settings.mrc || "",
          topMsg: this.settings.topMsg || "",
          btmMsg: this.settings.btmMsg || "",
        };
      }
      return {};
    },
    updateEbmSettingsSchema() {
      return updateEbmSettingsSchema;
    },
  },
  methods: {
    handleRdbChange(file) {
      this.rdb = Array.isArray(file) ? file[0] : file;
      this.errors.rdb = null;
    },
    handleOtherChange(files) {
      this.otherFiles = files;
    },
    onSubmit(values) {
      if (!this.rdb) {
        this.errors.rdb = "Please upload RDB Certificate";
        return;
      }

      const formData = new FormData();
      const payload = {
        ...values,
        ebm_activated: this.formData.ebm_activated,
        vat_activated: this.formData.vat_activated,
      };
      Object.entries(payload).forEach(([key, value]) => formData.append(key, value));

      if (this.rdb) formData.append("documents", this.rdb);

      const otherArr = Array.isArray(this.otherFiles)
        ? this.otherFiles
        : this.otherFiles
          ? Array.from(this.otherFiles)
          : [];
      otherArr.slice(0, 2).forEach((file) => formData.append("documents", file));

      this.$emit("update-settings", formData);
    },
  },
  created() {
    if (this.settings) {
      this.formData = {
        tin: this.settings.tin || "",
        bhfId: this.settings.bhfId || "",
        dvcSrlNo: this.settings.dvcSrlNo || "",
        mrc: this.settings.mrc || "",
        ebm_activated: this.settings.ebm_activated ?? false,
        vat_activated: this.settings.vat_activated ?? false,
        topMsg: this.settings.topMsg || "",
        btmMsg: this.settings.btmMsg || "",
      };
    }
  },
};
</script>

<style scoped>
.ebm-form {
  padding: 0.5rem 0;
}

.ebm-form__content {
  display: grid;
  gap: 1.1rem;
}

.ebm-form__toggles {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr 1fr;
  background: color-mix(in srgb, var(--hs-bg-soft) 64%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  padding: 1rem 1.1rem;
}

@media (max-width: 480px) {
  .ebm-form__toggles {
    grid-template-columns: 1fr;
  }
}

.ebm-form__files {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 560px) {
  .ebm-form__files {
    grid-template-columns: 1fr;
  }
}

.ebm-form__file-error {
  color: var(--hs-danger, #b91c1c);
  font-size: 0.78rem;
  margin-top: 0.35rem;
}

.ebm-form__submit {
  justify-self: end;
}
</style>
